// BookingModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select, { components } from 'react-select';
import moment from 'moment';
import Modal from "react-modal";
import { bookingService, companyService, locationService, emailService } from '../../api/apiService';
import calendarIcon from '../../images/calendar.svg';

// Set the app element for Modal accessibility
Modal.setAppElement('#root');

const BookingModal = ({ isOpen, onClose, selectedDate, selectedTalentId, selectedFunktion, selectedTalentName, selectedTalentSet, selectedTalentEmail, talentRate }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Handle selectedDate properly - it could be a Date object or a string
  const formattedDate = useMemo(() => {
    if (!selectedDate) return moment().format("YYYY-MM-DD");
    
    // If it's already a Date object (from talent calendar), use it directly
    if (selectedDate instanceof Date) {
      return moment(selectedDate).format("YYYY-MM-DD");
    }
    
    // If it's a string, try to parse it with the expected format
    if (typeof selectedDate === 'string') {
      const parsed = moment(selectedDate, "DD.MM.YY");
      if (parsed.isValid()) {
        return parsed.format("YYYY-MM-DD");
      }
      // Fallback: try parsing as ISO string or other formats
      const fallback = moment(selectedDate);
      if (fallback.isValid()) {
        return fallback.format("YYYY-MM-DD");
      }
    }
    
    // If all else fails, use today's date
    console.warn('Invalid selectedDate provided to BookingModal:', selectedDate);
    return moment().format("YYYY-MM-DD");
  }, [selectedDate]);

  const [startDate, setStartDate] = useState(formattedDate);
  const [endDate, setEndDate] = useState(formattedDate);
  const [status, setStatus] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kpi, setKpi] = useState('');
  const [set, setSet] = useState('');
  const [segment, setSegment] = useState('');
  const [range, setRange] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState(null);
  const [sinfo, setSinfo] = useState('');
  const [bookingoption, setbookingoption] = useState();
  const [client, setClient] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState({label: selectedFunktion, value: selectedFunktion});
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [selectedCostCentertwo, setSelectedCostCentertwo] = useState(null);
  const [hideinfos, setHideinfos] = useState(false);
  const [isChecked1, setIsChecked1] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Refs for date inputs
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // Update dates when formattedDate changes
  useEffect(() => {
    setStartDate(formattedDate);
    setEndDate(formattedDate);
  }, [formattedDate]);

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000); // Get all locations
      
      // Handle different possible response formats
      let locationsData = [];
      if (response.data?.locations) {
        locationsData = response.data.locations;
      } else if (Array.isArray(response.data)) {
        locationsData = response.data;
      }
      
      // Map locations to the required format
      const formattedLocations = locationsData
        .map(location => ({
          value: location.id || location.short || location,
          label: location.name || location.short || location
        }))
        .filter(location => location.value && location.label) // Filter out invalid entries
        .sort((a, b) => {
          if (!a.label || !b.label) return 0;
          return a.label.toString().localeCompare(b.label.toString());
        });
      
      console.log('Formatted locations:', formattedLocations);
      setLocations(formattedLocations);
    } catch (error) {
      console.error("Error fetching locations:", error);
      // Fallback to default locations if API fails
      setLocations([
        { value: 'Hamburg', label: 'Hamburg' },
        { value: 'Berlin', label: 'Berlin' },
        { value: 'Munich', label: 'Munich' }
      ]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.data && Array.isArray(response.data.companies)) {
        const companyNames = response.data.companies.map(company => company.name);
        setCompanies(companyNames);
      } else {
        console.error("Unexpected company response format:", response);
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  const locationOptions = useMemo(() => {
    if (!Array.isArray(locations)) return [];
    return locations;
  }, [locations]);

  const customStyles = {
    dropdownIndicator: (provided, state) => ({
      ...provided,
      border: 'none',
    }),
    control: (base) => ({
      ...base,
      width: '460px',
      borderColor: 'black',
      borderWidth: '1px',
      boxShadow: 'none',
      marginTop: '5px', marginBottom: '5px',
      borderRadius: '0px',
      color: 'black',
      fontWeight: "700",
      height: '20px',
      textTransform: 'uppercase',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
    input: (provided) => ({
      ...provided,
      color: 'black',
      fontWeight: "700"
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'black' : 'inherit',
      '&:hover': { backgroundColor: state.isSelected ? 'black' : '#f7f7f7' }
    }),
  };

  const DropdownIndicator = props => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <div style={{ width: '16px', margin: '0' }}>
            <svg version="1.1" id="Isolationsmodus" x="0px" y="0px" viewBox="0 0 322.9 257.78" width="16" height="16" shapeRendering="geometricPrecision">
              <path d="M208.27,7.37c-4.69-4.79-11.12-7.4-17.72-7.81c-0.4-0.03-0.8-0.05-1.22-0.05l0,0h-35.34h-70.2H31.14h-4.27  C17.33-0.46,7.87,4.51,3.34,13.12C-1.19,21.65-1.3,32.3,4.19,40.47c0.85,1.3,1.74,2.56,2.6,3.82C17.3,59.73,27.77,75.2,38.27,90.64  c12.47,18.33,24.91,36.7,37.39,55.03c3.08,4.53,6.13,9.02,9.21,13.54c3.97,5.86,9.06,10.5,16.08,12.36  c6.87,1.86,14.85,0.93,20.9-2.93c3.79-2.45,6.24-5.12,8.8-8.79c8.06-11.54,15.82-23.3,23.76-34.96  c13.14-19.33,26.25-38.67,39.39-57.96c4.68-6.87,9.32-13.73,14-20.63c4.31-6.31,8.39-12.13,8.2-20.41  C215.85,18.95,213.1,12.34,208.27,7.37z" />
            </svg>
          </div>
        </components.DropdownIndicator>
      )
    );
  };

  const constcenter1 = [
    {
      id: "714",
      label: "Post Outsourcing",
      accountNumber: "70175",
      value: "714",
    },
    {
      id: "715",
      label: "Virtual Production-Operator",
      accountNumber: "70155",
      value: "715",
    },
    { id: "716", label: "Videograf", accountNumber: "70145", value: "716" },
    { id: "717", label: "Foto Still", accountNumber: "70110", value: "717" },
    { id: "718", label: "Styling Still", accountNumber: "70111", value: "718" },
    { id: "719", label: "Foto Life", accountNumber: "70120", value: "719" },
    { id: "720", label: "Styling Life", accountNumber: "70121", value: "720" },
    {
      id: "721",
      label: "Foto-Assistenz Life",
      accountNumber: "70122",
      value: "721",
    },
    { id: "722", label: "Models", accountNumber: "70124", value: "722" },
    {
      id: "723",
      label: "Hair & Make up",
      accountNumber: "70130",
      value: "723",
    },
    { id: "724", label: "Art Direction", accountNumber: "70160", value: "724" },
    {
      id: "725",
      label: "Post Production",
      accountNumber: "70170",
      value: "725",
    },
    { id: "726", label: "Text", accountNumber: "70180", value: "726" },
    {
      id: "727",
      label: "Sonstiges Produktion",
      accountNumber: "70190",
      value: "727",
    },
    {
      id: "728",
      label: "Verpflegung Shooting",
      accountNumber: "70193",
      value: "728",
    },
    { id: "729", label: "Producing", accountNumber: "70230", value: "729" },
    { id: "730", label: "Modelbooking", accountNumber: "70240", value: "730" },
    { id: "731", label: "Art Buying", accountNumber: "70250", value: "731" },
    {
      id: "732",
      label: "Logistik/Warenaufbereitung",
      accountNumber: "70260",
      value: "732",
    },
    {
      id: "733",
      label: "Technical Support",
      accountNumber: "70270",
      value: "733",
    },
    { id: "734", label: "Setbau", accountNumber: "70280", value: "734" },
    {
      id: "735",
      label: "Account Management",
      accountNumber: "70320",
      value: "735",
    },
    {
      id: "736",
      label: "Sales/Marketing",
      accountNumber: "70440",
      value: "736",
    },
    { id: "737", label: "IT/Technik", accountNumber: "70450", value: "737" },
    {
      id: "739",
      label: "Still Operator",
      accountNumber: "70115",
      value: "739",
    },
    {
      id: "845",
      label: "Digital Operator",
      accountNumber: "70150",
      value: "845",
    },
    {
      id: "860",
      label: "AI Content Production",
      accountNumber: "70199",
      value: "860",
    },
  ];
  
  const constcenter2 = [
    {
      id: "738",
      label: "Kunden-übergreifend / Sonstiges",
      accountNumber: "709005",
      value: "738",
    },
    {
      id: "740",
      label: "S.Oliver Produktfoto",
      accountNumber: "700021",
      value: "740",
    },
    {
      id: "741",
      label: "Comma Produktfoto",
      accountNumber: "700031",
      value: "741",
    },
    {
      id: "742",
      label: "Liebeskind Produktfoto",
      accountNumber: "700041",
      value: "742",
    },
    {
      id: "743",
      label: "Görtz Produktfoto",
      accountNumber: "700051",
      value: "743",
    },
    {
      id: "744",
      label: "PUMA Produktfoto",
      accountNumber: "700061",
      value: "744",
    },
    {
      id: "745",
      label: "PUMA Kampagne",
      accountNumber: "700062",
      value: "745",
    },
    {
      id: "746",
      label: "TomTailor Produktfoto",
      accountNumber: "700071",
      value: "746",
    },
    {
      id: "747",
      label: "TomTailor Kamagne",
      accountNumber: "700072",
      value: "747",
    },
    {
      id: "748",
      label: "BonPrix Produktfoto",
      accountNumber: "700091",
      value: "748",
    },
    {
      id: "749",
      label: "HFG Produktfoto",
      accountNumber: "700101",
      value: "749",
    },
    { id: "750", label: "HFG Kampagne", accountNumber: "700102", value: "750" },
    {
      id: "751",
      label: "Juvia Produktfoto",
      accountNumber: "700111",
      value: "751",
    },
    {
      id: "752",
      label: "HugoBoss Produktfoto",
      accountNumber: "700121",
      value: "752",
    },
    {
      id: "753",
      label: "HugoBoss Kampagne",
      accountNumber: "700122",
      value: "753",
    },
    {
      id: "754",
      label: "Bogner Produktfoto",
      accountNumber: "700601",
      value: "754",
    },
    {
      id: "755",
      label: "Bogner Kampagne",
      accountNumber: "700602",
      value: "755",
    },
    {
      id: "756",
      label: "Heine Produktfoto",
      accountNumber: "700621",
      value: "756",
    },
    {
      id: "757",
      label: "WittWeiden Produktfoto",
      accountNumber: "705071",
      value: "757",
    },
    {
      id: "758",
      label: "Zalando EPS Produktfoto",
      accountNumber: "705141",
      value: "758",
    },
    {
      id: "759",
      label: "Zalando EPS Kampagne",
      accountNumber: "705142",
      value: "759",
    },
    {
      id: "760",
      label: "ToryBurch Produktfoto",
      accountNumber: "705171",
      value: "760",
    },
    {
      id: "761",
      label: "Falke Produktfoto",
      accountNumber: "705181",
      value: "761",
    },
    {
      id: "762",
      label: "Zalando RPDP Produktfoto",
      accountNumber: "705221",
      value: "762",
    },
    {
      id: "763",
      label: "Sonstige Kleinkunden Produktfoto",
      accountNumber: "707991",
      value: "763",
    },
    {
      id: "766",
      label: "PUMA Virtual Production",
      accountNumber: "700066",
      value: "766",
    },
    {
      id: "767",
      label: "C&A Produktfoto",
      accountNumber: "703421",
      value: "767",
    },
    { id: "768", label: "C&A Kampagne", accountNumber: "703422", value: "768" },
    {
      id: "769",
      label: "C&A Virtual Production",
      accountNumber: "703429",
      value: "769",
    },
    {
      id: "773",
      label: "Ziener Produktfoto",
      accountNumber: "705051",
      value: "773",
    },
    {
      id: "772",
      label: "Zalando EPDP Produktfoto HH",
      accountNumber: "705191",
      value: "772",
    },
    {
      id: "778",
      label: "Tommy Hilfiger Produktfoto",
      accountNumber: "740411",
      value: "778",
    },
    {
      id: "847",
      label: "z PDP ACC",
      accountNumber: "705230",
      value: "847",
    },
    {
      id: "848",
      label: "Mammut PF HH ",
      accountNumber: "700551",
      value: "848",
    },
    {
      id: "856",
      label: "TB International PF HH",
      accountNumber: "700451",
      value: "856",
    },
    {
      id: "859",
      label: "Marketplace Amplifier GmbH Produktfoto HH",
      accountNumber: "700452",
      value: "859",
    },
    {
      id: "849",
      label: "DEV - AI Character Creation",
      accountNumber: "709510",
      value: "849",
    },
    {
      id: "850",
      label: "DEV - AI Character Replacement",
      accountNumber: "709512",
      value: "850",
    },
    {
      id: "852",
      label: "DEV - AI Background Replacement",
      accountNumber: "709516",
      value: "852",
    },
    {
      id: "853",
      label: "DEV - AI Background Replacement",
      accountNumber: "709518",
      value: "853",
    },
    {
      id: "854",
      label: "DEV - Bust to on-Model",
      accountNumber: "709520",
      value: "854",
    },
    {
      id: "855",
      label: "DEV - AI Content Hub",
      accountNumber: "709522",
      value: "855",
    },
    {
      id: "861",
      label: "DEV - KIND",
      accountNumber: "709524",
      value: "861",
    },
  
  ];

  const bookingStatusOptions = [
    { value: "Booked", label: "booked / Lano task created" },
    { value: "confirmed", label: "confirmed" },
    { value: "new order", label: "new order" },
    { value: "requested", label: "requested" },
    { value: "declined request", label: "declined request" },
    { value: "declined", label: "declined" },
    { value: "canceled", label: "canceled" },
    { value: "optioned", label: "optioned" },
    { value: "optionSolved", label: "option released" },
    { value: "talentOnHold", label: "1. Option" },
  ];

  const handleSelectCostCenter = (selectedOption) => {
    setSelectedCostCenter(selectedOption);
  };
  const handleSelectCostCentertwo = (selectedOption) => {
    setSelectedCostCentertwo(selectedOption);
  };

  const companyOptions = useMemo(() => 
    companies.map(company => ({
      value: company,
      label: company
    })).sort((a, b) => a.label.localeCompare(b.label)),
    [companies]
  );

  const options = [
    { value: 'new order', label: 'New order' },
    { value: 'optioned', label: 'Optioned' },
    { value: 'talentaccepted', label: 'Accepted by talent' },
    { value: 'confirmed', label: 'Confirmed' }
  ];

  const position = [
    { value: 'HM', label: 'Hair & Make-Up' },
    { value: 'AD', label: 'Art Direction' },
    { value: 'foto', label: 'Photo' },
    { value: 'foto-assistent', label: 'Photo Assistant' },
    { value: 'video', label: 'Video' },
    { value: 'digi-op', label: 'Digi-Op' },
    { value: 'styling', label: 'Styling' },
    { value: 'styling-assistent', label: 'Styling Assistant' },
    { value: "LOGISTIK / WARENAUFBEREITUNG", label: "LOGISTIK / WARENAUFBEREITUNG" },
    { value: "PRODUCER", label: "Producer" },
    { value: "Art Buying", label: "Art Buying" },
    { value: "Account Management", label: "Account Management" }, 
  ];

  const onmodel = [
    { value: 'onmodel', label: 'On Model' },
    { value: 'Produkt', label: 'On Product' },
  ];

  const bookingoptions = [
    { value: 'booking', label: 'Booking' },
    { value: 'option', label: 'Option' },
  ];

  useEffect(() => {
    fetchLocations();
    fetchCompanies();
  }, []);

  const handleCompanyChange = (selectedOption) => {
    setSelectedCompany(selectedOption);
  };

  const handlesetSelect = (selectedOption) => {
    setSet(selectedOption);
  };

  const handleClientChange = (selectedOption) => {
    setClient(selectedOption);
    if (selectedOption?.value === 'Set Cast 1. Option') {
      setHideinfos(true);
      setSelectedCostCenter(null);
      setSelectedCostCentertwo(null);
    } else {
      setHideinfos(false);
    }
  };

  const handleOptionChange = (selectedOption) => {
    setbookingoption(selectedOption);
  };

  const handlePostionchange = (selectedOption) => {
    setSelectedPosition(selectedOption);
  };
  const handleStatusChange = (selectedOption) => {
    setStatus(selectedOption);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Automatically create multiple bookings if dates are different
      if (endDate && endDate !== startDate) {
        // Validate dates before processing
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        
        // Check if dates are valid
        if (!startMoment.isValid()) {
          alert('Invalid start date selected');
          setIsLoading(false);
          return;
        }
        
        if (!endMoment.isValid()) {
          alert('Invalid end date selected');
          setIsLoading(false);
          return;
        }
        
        // Ensure end date is after start date
        if (endMoment.isBefore(startMoment)) {
          alert('End date must be after start date');
          setIsLoading(false);
          return;
        }
        
        const daysDifference = endMoment.diff(startMoment, 'days');
        
        // Validate daysDifference
        if (isNaN(daysDifference) || daysDifference < 0) {
          console.error('Invalid days difference:', daysDifference);
          alert('Invalid date range selected');
          setIsLoading(false);
          return;
        }
        
        console.log(`Creating ${daysDifference + 1} bookings for date range`);
        
        // Create array of promises for each day
        const bookingPromises = [];
        
        for (let i = 0; i <= daysDifference; i++) {
          const currentDate = moment(startDate).add(i, 'days').format('YYYY-MM-DD');
          
          const bookingData = {
            title: `${selectedFunktion} - ${selectedTalentName}`,
            talent_id: selectedTalentId,
            start_date: currentDate,
            end_date: currentDate,
            status: status.value,
            kpi: kpi,
            set: selectedTalentSet,
            set_desc: description,
            position: { label: selectedFunktion, value: selectedFunktion },
            client: client,
            sonstige_info: sinfo,
            studio: description,
            cost_center1: selectedCostCenter,
            cost_center2: selectedCostCentertwo,
            start_time: startTime,
            end_time: endTime,
            location: selectedLocations?.value,
            proposed_by: localStorage.getItem("userEmail")
          };

          bookingPromises.push(bookingService.createBooking(bookingData));
        }
        
        // Execute all booking creation promises
        await Promise.all(bookingPromises);
        console.log(`Successfully created ${bookingPromises.length} bookings`);
        
        // Send email notifications if checked
        if (isChecked1) {
          const firstBookingData = {
            title: `${selectedFunktion} - ${selectedTalentName}`,
            talent_id: selectedTalentId,
            start_date: startDate,
            end_date: endDate,
            status: status.value,
            kpi: kpi,
            set: selectedTalentSet,
            set_desc: description,
            position: { label: selectedFunktion, value: selectedFunktion },
            client: client,
            sonstige_info: sinfo,
            studio: description,
            cost_center1: selectedCostCenter,
            cost_center2: selectedCostCentertwo,
            start_time: startTime,
            end_time: endTime,
            location: selectedLocations?.value,
            proposed_by: localStorage.getItem("userEmail")
          };
          await sendEmailNotification(firstBookingData);
        }

        alert(`Successfully created ${bookingPromises.length} bookings for multiple days!`);
      } else {
        // Single day booking (original behavior)
        const bookingData = {
          title: `${selectedFunktion} - ${selectedTalentName}`,
          talent_id: selectedTalentId,
          start_date: moment(selectedDate).format('YYYY-MM-DD'),
          end_date: moment(selectedDate).format('YYYY-MM-DD'),
          status: status.value,
          kpi: kpi,
          set: selectedTalentSet,
          set_desc: description,
          position: { label: selectedFunktion, value: selectedFunktion },
          client: client,
          sonstige_info: sinfo,
          studio: description,
          cost_center1: selectedCostCenter,
          cost_center2: selectedCostCentertwo,
          start_time: startTime,
          end_time: endTime,
          location: selectedLocations?.value,
          proposed_by: localStorage.getItem("userEmail")
        };

        const response = await bookingService.createBooking(bookingData);

        if (isChecked1) {
          await sendEmailNotification(bookingData);
        }

        alert("Booking created successfully.");
      }
      
      onClose();
    } catch (error) {
      console.error("Error creating booking:", error);
      alert("Error creating booking. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailNotification = async (bookingData) => {
    try {
      const emailData = {
        to_email: ['booking@set-cast.com','talents@set-cast.com', selectedTalentEmail],
        subject: `${status.value}- ${client.label} - ${selectedTalentName}`,
        content: `
          Dear ${selectedTalentName},
          
          <p>STATUS: ${status.value}</p>
          <p>FUNCTION: ${selectedFunktion}</p>
          <p>CLIENT: ${client.label}</p>
          <p>Set: ${selectedTalentSet}</p>
          <p>DATE/PERIOD: ${moment(selectedDate).format('YYYY-MM-DD')}</p>
          <p>TIME: ${startTime} - ${endTime}</p>
          <p>DAYRATE: ${talentRate}</p>
          <p>KPI: ${kpi}</p>
          <p>LOCATION: ${selectedLocations?.value}</p>
          <p>SETCast contact person: ${localStorage.getItem("userEmail")}</p>

          <p>In case of illness or delay, please send an email directly to the distribution list planning@orendtstudios.com and krank@set-cast.com by 8:00 a.m. at the latest.</p>
          <p>Please make sure to inform Set-Cast and the producer of the production if you need to leave the set early and double check with the producer if the KPI has been reached.</p>
          <p>For questions regarding KPI, shooting schedules, callsheets and briefings, please contact planning@orendtstudios.com.</p>
          <p>Invoice details:</p>
          <p>please upload your invoice in Lano - you could combine several tasks in one invoice</p>
          <p>Check all the booking in <a href="https://app.set-cast.com/">https://app.set-cast.com/</a></p>
          
          
          <p>LEGENDE:</p>
          <p>B28 = Behringstrasse 28a, 22765 Hamburg</p>
          <p>TR4 = Tarpenring 4, 22419 Hamburg</p>
          <p>M= Holy-Allee 3, Metzingen , am Empfang melden</p>
          <p>OSB= Am Rosenkothen 2, 40880 Ratingen</p>
        `,
        cc: ['booking@set-cast.com']
      };

      await emailService.sendEmail(emailData);
    } catch (error) {
      console.error("Error sending email notification:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Create Booking"
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 9999,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        content: {
          position: 'relative',
          top: 'auto',
          left: 'auto',
          right: 'auto',
          bottom: 'auto',
          margin: '20px',
          border: '1px solid #ccc',
          background: '#fff',
          overflow: 'auto',
          borderRadius: '4px',
          outline: 'none',
          padding: '20px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }
      }}
    >
      <div className="modal-container">
        <button 
          className="modal-close" 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          ×
        </button>
        <h3>Create Order</h3>

        <p>FUNCTION: {selectedFunktion}</p>
        <p>set: {selectedTalentSet}</p>
        <p>DATE: {formattedDate}</p>
        <p>Talent: {selectedTalentName}</p>
        <p>Rate: {talentRate}</p>

        <form onSubmit={handleSubmit}>
          <Select
            components={{ DropdownIndicator }}
            options={bookingStatusOptions}
            onChange={handleStatusChange}
            placeholder="Status"
            value={status}
            styles={customStyles}
          />

          <Select
            components={{ DropdownIndicator }}
            options={locationOptions}
            onChange={setSelectedLocations}
            placeholder="Locations"
            value={selectedLocations}
            styles={customStyles}
          />

          <Select
            components={{ DropdownIndicator }}
            options={companyOptions}
            onChange={handleClientChange}
            placeholder="Client"
            value={client}
            styles={customStyles}
          />

          {!hideinfos && (
            <>
              <input
                type="text"
                className="inputtextmodal"
                value={kpi}
                onChange={(e) => setKpi(e.target.value)}
                placeholder="KPI"
              />

              <input
                type="text"
                className="inputtextmodal"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Studio"
              />

              <input
                type="text"
                className="inputtextmodal"
                value={sinfo}
                onChange={(e) => setSinfo(e.target.value)}
                placeholder="S. Info"
              />
            </>
          )}

          <input
            type="text"
            className="inputtextmodal"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            placeholder="Start Time"
          />
          <input
            type="text"
            className="inputtextmodal"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            placeholder="End Time"
          />

          {/* Date Fields - Always Visible */}
          <div style={{ marginTop: '15px', marginBottom: '15px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>From Date:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    ref={startDateRef}
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 35px 8px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <img 
                    src={calendarIcon}
                    alt="Calendar"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      opacity: 0.7,
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                    onClick={() => {
                      if (startDateRef.current) {
                        startDateRef.current.focus();
                        startDateRef.current.showPicker?.();
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>To Date:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    ref={endDateRef}
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    style={{
                      width: '100%',
                      padding: '8px 35px 8px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <img 
                    src={calendarIcon}
                    alt="Calendar"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      opacity: 0.7,
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                    onClick={() => {
                      if (endDateRef.current) {
                        endDateRef.current.focus();
                        endDateRef.current.showPicker?.();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex' }}>
            <div className="custom-checkbox" style={{ marginBottom: '20px' , marginRight: '12px'}}>
              <input
                type="checkbox"
                id="checkbox-1"
                checked={isChecked1}
                onChange={() => setIsChecked1(!isChecked1)}
              />
              <label htmlFor="checkbox-1" > <span style={{ marginLeft: '12px' }}>Send Email notification to Talent</span></label>
            </div>
          </div>

          <button className="button" type="submit" disabled={isLoading}>
            {isLoading ? 'Creating...' : (endDate && endDate !== startDate ? 'Create Multiple Bookings' : 'Create Order')}
          </button>
          <button className="button" onClick={onClose} disabled={isLoading}>
            Close
          </button>
        </form>
      </div>
    </Modal>
  );
};

export default BookingModal;
